// Catalog.ID Client — Ceremony Console
// ============================================================================
// Where an organization — or a person — signs what the Developer's `cl-root`
// needs from them (docs/cid-governance.md M6, §4.6):
//
//   • countersign the certificate requests a node's setup wrote (`cpr`, `cid`);
//   • sign the organization's binding consent to an operator code;
//   • sign an account's voluntary origin migration consent, or the voluntary
//     bulk migration consent of an operator's organization.
//
// NO NAVIGATION ENTRY EXISTS, BY DESIGN. The ceremony never arrives
// unannounced: the operator runbook sends the owner here by the documented
// deep link `/ceremony` (app.ts), so this view is summoned, not discovered. Do
// not add it to a workspace layout or to `recommendedPanes`. It is not gated
// on this node's own code: an operator without a Catalog.ID node signs at
// another operator's node.
//
// Everything that decides what may be signed lives in ../ceremony/* and is
// unit-tested; this file is the surface: intake (a delivered file) or
// preparation (a consent built here), review (the renderer, the ledger
// context, the signer gate), signing, and the download for the Developer.

import { createElement, createFieldGroup, createFormRow } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext } from '@catalog-shell';
import {
    readCeremonyInput,
    renderCeremonyDocument,
    type CeremonyDocument,
    type CeremonyRendering,
    type RenderedKey,
} from '../ceremony/payload';
import {
    MAIN_SIGNING_KEY,
    ceremonySignatureVerifier,
    loadCeremonySession,
    loadOrgSigningSeed,
    resolveCeremonySigner,
    signCeremonyDocument,
    signerNeedOf,
    type CeremonySession,
    type SignedCeremonyFile,
} from '../ceremony/signer';
import {
    fetchCurrentSigningKey,
    fetchNodeCode,
    fetchOperatorRecord,
    gatherLedgerFacts,
    judgeLedgerContext,
    type LedgerFacts,
} from '../ceremony/ledger-context';
import {
    invalidCode,
    normalizeOperatorCode,
    prepareAccountMigrationConsent,
    prepareBindingConsent,
    prepareBulkMigrationConsent,
    type Prepared,
} from '../ceremony/prepare';
import { downloadBlob } from '../components/pdf-loader';
import { PANE_IDS } from '../shell-config';

export function createCeremonyConsolePane(): PaneType {
    return {
        typeId: PANE_IDS.ceremonyConsole,
        kind: 'leaf',
        defaultTitle: 'Ceremony Console',
        capabilities: [],
        render: (_ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            renderCeremonyConsole(container);
            return container;
        },
    };
}

/** Full-page entry for the documented `/ceremony` deep link. */
export function renderCeremonyConsoleStandalone(container: HTMLElement): void {
    container.innerHTML = '';
    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card' });
    card.style.width = '680px';
    card.appendChild(createElement('h2', { className: 'cid-auth-title', textContent: 'Ceremony Console' }));

    const body = createElement('div');
    card.appendChild(body);
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    renderCeremonyConsole(body);
}

// ── shared bits ─────────────────────────────────────────────────────────

function errorLine(parent: HTMLElement): { show(message: string): void; clear(): void } {
    // `.cid-pane-error` is display:none in the stylesheet; `--visible` reveals it.
    const el = createElement('p', { className: 'cid-pane-error' });
    parent.appendChild(el);
    return {
        show(message) {
            el.textContent = message;
            el.classList.add('cid-pane-error--visible');
        },
        clear() {
            el.textContent = '';
            el.classList.remove('cid-pane-error--visible');
        },
    };
}

/** A fingerprint in its D17 form: sixteen groups on two lines, never truncated. */
function fingerprintBlock(fingerprint: string): HTMLElement {
    return createElement('pre', { className: 'cid-ceremony-fingerprint', textContent: fingerprint });
}

function keyRows(title: string, keys: readonly RenderedKey[]): HTMLElement {
    const group = createFieldGroup({ title, labelSize: 'l' });
    for (const k of keys) {
        const row = createFormRow({ label: k.label });
        if (k.detail) row.content.appendChild(createElement('div', { className: 'cid-pane-note', textContent: k.detail }));
        row.content.appendChild(fingerprintBlock(k.fingerprint));
        group.body.appendChild(row.element);
    }
    return group.element;
}

function warning(parent: HTMLElement, message: string): void {
    parent.appendChild(createElement('p', { className: 'cid-wizard-warning', textContent: message }));
}

// ── step 1: open a file, or prepare a consent ───────────────────────────

function renderCeremonyConsole(root: HTMLElement): void {
    root.innerHTML = '';
    const loading = createElement('p', { className: 'cid-wizard-text', textContent: 'Reading your session…' });
    root.appendChild(loading);

    // The session comes from the node, roles included — never from the
    // identity state a login leaves behind, which has no roles (M6).
    void loadCeremonySession().then(
        session => renderIntake(root, session),
        () => {
            root.innerHTML = '';
            warning(root, 'You are not signed in. Sign in as the organization\'s owner delegate (or as the account '
                + 'that signs), then open this page again.');
        },
    );
}

function renderIntake(root: HTMLElement, session: CeremonySession): void {
    root.innerHTML = '';
    root.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: `Signed in as ${session.cid} (${session.accountType}`
            + `${session.roles.length > 0 ? `, ${session.roles.join(', ')}` : ''}).`,
    }));

    renderOpenFile(root, session);
    renderPrepare(root, session);
}

function renderOpenFile(root: HTMLElement, session: CeremonySession): void {
    // Drag-and-drop listeners hang off this per-render panel, not off `root`:
    // `root` survives every re-render, so listeners bound to it would stack up.
    const panel = createElement('div', { className: 'cid-ceremony-drop' });
    root.appendChild(panel);

    panel.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Open a file' }));
    panel.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Open a certificate request your node\'s setup wrote (…-certificate-request.json), or a consent '
            + 'you were asked to sign — drop it here or choose it below. You see exactly what it says before anything '
            + 'is signed.',
    }));

    const fileInput = createElement('input', {
        className: 'cid-auth-field',
        attributes: { type: 'file', accept: 'application/json,.json', 'aria-label': 'Ceremony file to open' },
    });
    panel.appendChild(fileInput);

    panel.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Or paste its text exactly as it is, without reformatting it:',
    }));
    const textarea = createElement('textarea', {
        className: 'cid-auth-field',
        attributes: { rows: '5', spellcheck: 'false', 'aria-label': 'Ceremony file' },
    });
    textarea.style.width = '100%';
    textarea.style.fontFamily = 'monospace';
    panel.appendChild(textarea);

    const error = errorLine(panel);
    const reviewBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: 'Review file' });
    const actions = createElement('div', { className: 'cid-dashboard-actions' });
    actions.appendChild(reviewBtn);
    panel.appendChild(actions);

    const submit = (input: string | Uint8Array): void => {
        error.clear();
        void readCeremonyInput(input, ceremonySignatureVerifier).then(result => {
            if (!result.ok) {
                error.show(result.refusal.message);
                return;
            }
            renderReview(root, session, result.document);
        }, (e: unknown) => error.show(e instanceof Error ? e.message : 'That file could not be read.'));
    };

    reviewBtn.addEventListener('click', () => submit(textarea.value));

    // A file is read as bytes: the canonical check is on the bytes that arrived.
    const submitFile = (file: File): void => {
        void file.arrayBuffer()
            .then(buf => submit(new Uint8Array(buf)))
            .catch(() => error.show('That file could not be read.'));
    };
    fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (file) submitFile(file);
    });

    const stop = (e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); };
    panel.addEventListener('dragover', (e) => { stop(e); panel.classList.add('cid-ceremony-drop--active'); });
    panel.addEventListener('dragleave', (e) => { stop(e); panel.classList.remove('cid-ceremony-drop--active'); });
    panel.addEventListener('drop', (e) => {
        stop(e);
        panel.classList.remove('cid-ceremony-drop--active');
        const file = e.dataTransfer?.files?.[0];
        if (file) submitFile(file);
    });
}

function renderPrepare(root: HTMLElement, session: CeremonySession): void {
    const panel = createElement('div');
    root.appendChild(panel);
    panel.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Prepare a consent' }));

    const asOrganization = resolveCeremonySigner(session, { kind: 'organization', orgCid: null });
    const personal = session.accountType === 'natural_person';
    if (!asOrganization.eligible && !personal) {
        warning(panel, asOrganization.message);
        return;
    }

    const error = errorLine(panel);
    const prepared = <T>(result: Prepared<T>, toDocument: (consent: T) => CeremonyDocument): void => {
        if (!result.ok) {
            error.show(result.message);
            return;
        }
        renderReview(root, session, toDocument(result.consent));
    };
    const failed = (e: unknown): void => error.show(e instanceof Error ? e.message : 'The node could not be asked.');
    const field = (label: string, value = ''): HTMLInputElement => {
        const row = createFormRow({ label });
        const input = createElement('input', {
            className: 'cid-auth-field',
            attributes: { type: 'text', spellcheck: 'false', autocomplete: 'off', 'aria-label': label },
        }) as HTMLInputElement;
        input.value = value;
        row.content.appendChild(input);
        panel.appendChild(row.element);
        return input;
    };
    const actionButton = (label: string, onClick: () => void): void => {
        const btn = createElement('button', { className: 'cs-btn cs-btn--default', textContent: label });
        btn.addEventListener('click', () => { error.clear(); onClick(); });
        const actions = createElement('div', { className: 'cid-dashboard-actions' });
        actions.appendChild(btn);
        panel.appendChild(actions);
    };

    if (asOrganization.eligible) {
        const orgCid = asOrganization.signerCid;

        // ── binding consent (D6) ──
        panel.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: `Binding consent: ${orgCid} consents to be bound to an operator code, under its current `
                + 'signing key. The sequence comes from the ledger.',
        }));
        const bindCode = field('Operator code to bind');
        actionButton('Prepare binding consent', () => {
            const code = normalizeOperatorCode(bindCode.value);
            const bad = invalidCode(code);
            if (bad) {
                error.show(bad);
                return;
            }
            void Promise.all([fetchOperatorRecord(code), fetchCurrentSigningKey(orgCid)]).then(([record, key]) => {
                const result = prepareBindingConsent({ operatorId: code, orgCid, orgPublicKey: key, record, now: new Date() });
                prepared(result, consent => ({ kind: 'binding_consent', consent }));
            }, failed);
        });

        // ── bulk migration consent (D20) ──
        if (session.operatorCodes.length > 0) {
            panel.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: `Bulk migration consent: every account of a code bound to ${orgCid} `
                    + `(${session.operatorCodes.join(', ')}) moves to another operator.`,
            }));
            const from = field('Code whose accounts move', session.operatorCodes.length === 1 ? session.operatorCodes[0] : '');
            const to = field('Target operator code (bulk)');
            const seq = field('Bulk migration sequence', '1');
            actionButton('Prepare bulk migration consent', () => {
                void fetchCurrentSigningKey(orgCid).then(key => {
                    const result = prepareBulkMigrationConsent({
                        fromOperatorId: from.value, toOperatorId: to.value, sequence: Number(seq.value.trim()),
                        orgCid, orgPublicKey: key, boundCodes: session.operatorCodes,
                    });
                    prepared(result, consent => ({ kind: 'bulk_migration_consent', consent }));
                }, failed);
            });
        }
    }

    // ── the account's own migration consent (D20) ──
    const accountCid = personal ? session.cid : asOrganization.eligible ? asOrganization.signerCid : null;
    if (accountCid !== null) {
        panel.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: `Migration consent: ${accountCid} moves its origin from this node's operator to another one.`,
        }));
        const to = field('Target operator code');
        const seq = field('Migration sequence', '1');
        actionButton('Prepare migration consent', () => {
            void Promise.all([fetchNodeCode(), fetchCurrentSigningKey(accountCid)]).then(([from, key]) => {
                const result = prepareAccountMigrationConsent({
                    accountCid, fromOperatorId: from, toOperatorId: to.value, sequence: Number(seq.value.trim()),
                    accountPublicKey: key,
                });
                prepared(result, consent => ({ kind: 'account_migration_consent', consent }));
            }, failed);
        });
    }
}

// ── step 2: what it says, what the ledger says, who may sign ────────────

function renderReview(root: HTMLElement, session: CeremonySession, doc: CeremonyDocument): void {
    root.innerHTML = '';
    const status = createElement('p', { className: 'cid-wizard-text', textContent: 'Reading the document…' });
    root.appendChild(status);

    const back = (): HTMLElement => {
        const btn = createElement('button', { className: 'cs-btn cs-btn--default', textContent: 'Start over' });
        btn.addEventListener('click', () => renderCeremonyConsole(root));
        return btn;
    };

    void (async () => {
        const rendering = await renderCeremonyDocument(doc);
        status.remove();
        showRendering(root, rendering);

        const actions = createElement('div', { className: 'cid-dashboard-actions' });
        const signer = resolveCeremonySigner(session, signerNeedOf(doc));
        if (!signer.eligible) {
            warning(root, signer.message);
            actions.appendChild(back());
            root.appendChild(actions);
            return;
        }

        const ledgerStatus = createElement('p', { className: 'cid-pane-note', textContent: 'Reading what this node\'s ledger says…' });
        root.appendChild(ledgerStatus);
        let facts: LedgerFacts;
        try {
            facts = await gatherLedgerFacts(doc, signer.signerCid, session.operatorCodes);
        } catch (e) {
            ledgerStatus.remove();
            warning(root, `This node could not be asked what its ledger says (${e instanceof Error ? e.message : 'error'}). `
                + 'Nothing can be signed without it; try again.');
            actions.appendChild(back());
            root.appendChild(actions);
            return;
        }
        const judgement = await judgeLedgerContext(doc, facts);
        ledgerStatus.remove();

        if (judgement.keys.length > 0) root.appendChild(keyRows('The signing key and the ledger', judgement.keys));
        for (const note of judgement.notes) root.appendChild(createElement('p', { className: 'cid-pane-note', textContent: note }));
        if (judgement.blocking.length > 0) {
            for (const b of judgement.blocking) warning(root, b);
            actions.appendChild(back());
            root.appendChild(actions);
            return;
        }

        const error = errorLine(root);
        const label = rendering.signer === 'organization' ? 'Sign with the organization key' : 'Sign with your account key';
        const signBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: label });
        signBtn.addEventListener('click', () => {
            signBtn.setAttribute('disabled', 'true');
            signBtn.textContent = 'Signing…';
            error.clear();
            void sign(doc, signer.signerCid, signer.keySource, facts.currentKey).then(
                file => renderDone(root, rendering, file),
                (e: unknown) => {
                    signBtn.removeAttribute('disabled');
                    signBtn.textContent = label;
                    error.show(e instanceof Error ? e.message : 'Signing failed.');
                });
        });
        actions.appendChild(signBtn);
        actions.appendChild(back());
        root.appendChild(actions);
    })().catch((e: unknown) => {
        status.textContent = '';
        warning(root, e instanceof Error ? e.message : 'The document could not be shown.');
        root.appendChild(back());
    });
}

function showRendering(root: HTMLElement, rendering: CeremonyRendering): void {
    root.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: rendering.title }));
    root.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: rendering.summary }));

    const group = createFieldGroup({ title: 'What you sign', labelSize: 'l' });
    for (const f of rendering.facts) {
        const row = createFormRow({ label: f.label });
        row.content.appendChild(createElement('code', { className: 'cid-key-field-value', textContent: f.value }));
        group.body.appendChild(row.element);
    }
    root.appendChild(group.element);
    root.appendChild(keyRows('Keys it names', rendering.keys));
}

// ── step 3: sign, check, download ───────────────────────────────────────

async function sign(
    doc: CeremonyDocument, signerCid: string, keySource: 'keybag' | 'wrapped-org-key', currentKey: Uint8Array | null,
): Promise<SignedCeremonyFile> {
    const loaded = await loadOrgSigningSeed(keySource, MAIN_SIGNING_KEY);
    if (!loaded.ok) throw new Error(loaded.message);
    try {
        return await signCeremonyDocument(doc, { signerCid, secretSeed: loaded.secretSeed, currentKey }, ceremonySignatureVerifier);
    } finally {
        loaded.secretSeed.fill(0);
    }
}

function renderDone(root: HTMLElement, rendering: CeremonyRendering, file: SignedCeremonyFile): void {
    root.innerHTML = '';
    root.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Ready for the Developer' }));
    root.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: `Signed: ${rendering.title}. The file ${file.filename} has been downloaded; send it to the `
            + 'Developer as it is — never edit, reformat or retype it. Then read the fingerprints below to the Developer over a second '
            + 'channel (voice, chat), not in the same message as the file: the Developer types each one back before '
            + 'signing.',
    }));
    root.appendChild(keyRows('Fingerprints to read back', rendering.keys));

    // The file is downloaded as soon as it is signed and checked; the button
    // is for a download the browser lost.
    const save = (): void => downloadBlob(new Blob([file.bytes as BlobPart], { type: 'application/json' }), file.filename);
    save();
    const actions = createElement('div', { className: 'cid-dashboard-actions' });
    const downloadBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: 'Download again' });
    downloadBtn.addEventListener('click', save);
    const doneBtn = createElement('button', { className: 'cs-btn cs-btn--default', textContent: 'Done' });
    doneBtn.addEventListener('click', () => renderCeremonyConsole(root));
    actions.appendChild(downloadBtn);
    actions.appendChild(doneBtn);
    root.appendChild(actions);
}
